/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';

import {ApiConfig} from '../../../config/api-config';
import {AdnApiClientService} from '../../../common/controller/adn-api-client.service';
import {SignalAdminResourcePage} from '../../../common/controller/signal-admin-resource-api';

export type AdnInvoicingStatus = 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED';
export type AdnInvoicingUploadStatus = 'NOT_REQUESTED' | 'PENDING' | 'UPLOADING' | 'UPLOADED' | 'FAILED';

export interface AdnInvoicingProgressStep {
  status: 'DONE' | 'EXECUTING' | 'INCOMPLETE';
  step: number;
  total: number;
  description: string;
  durationMillis?: number;
}

export interface AdnInvoicingJob {
  id: string;
  networkId: number;
  marketplaceNetworkId: string;
  createUsername: string;
  masqueraderUsername?: string;
  createTime: string;
  updateTime?: string;
  periodType: 'MONTH' | 'WEEK' | 'CUSTOM';
  periodValue: string;
  status: AdnInvoicingStatus;
  completedSteps: number;
  totalSteps: number;
  progress: AdnInvoicingProgressStep[];
  totalDurationMillis?: number;
  resultAvailable: boolean;
  downloadUrl?: string;
  archiveName?: string;
  uploadStatus: AdnInvoicingUploadStatus;
  errorMessage?: string;
}

export interface AdnInvoicingPeriod {
  marketplaceNetworkId: string;
  month?: string;
  week?: string;
  startDate?: string;
  endDate?: string;
}

export interface AdnInvoicingEnqueueResult {
  job: AdnInvoicingJob;
  reused: boolean;
}

@Injectable()
export class AdnInvoicingApiService {
  private readonly baseUrl = this.apiConfig.getVersionedUri('adninvoicing');

  constructor(private readonly client: AdnApiClientService, private readonly apiConfig: ApiConfig) {
  }

  enqueue(period: AdnInvoicingPeriod, recreate = false): Promise<AdnInvoicingEnqueueResult> {
    return firstValueFrom(this.client.requestResponse({
      method: 'POST',
      url: this.baseUrl,
      params: recreate ? {...period, recreate: true} : period,
      body: {},
      hasErrorHandler: true,
      ignore404: true
    })).then((response) => ({job: response.data, reused: response.status === 200}));
  }

  get(id: string): Promise<AdnInvoicingJob> {
    return firstValueFrom(this.client.request<AdnInvoicingJob>({
      method: 'GET',
      url: `${this.baseUrl}/${encodeURIComponent(id)}`,
      hasErrorHandler: true,
      ignore404: true
    }));
  }

  query(page = 1, pageSize = 25): Promise<SignalAdminResourcePage<AdnInvoicingJob>> {
    return firstValueFrom(this.client.request<SignalAdminResourcePage<AdnInvoicingJob>>({
      method: 'GET',
      url: this.baseUrl,
      params: {page, pageSize},
      hasErrorHandler: true,
      ignore404: true
    }));
  }

  upload(id: string): Promise<AdnInvoicingJob> {
    return firstValueFrom(this.client.request<AdnInvoicingJob>({
      method: 'POST',
      url: `${this.baseUrl}/${encodeURIComponent(id)}/upload`,
      body: {},
      hasErrorHandler: true,
      ignore404: true
    }));
  }

  download(job: AdnInvoicingJob): Promise<Blob> {
    const url = job.downloadUrl
      ? this.apiConfig.getUri(job.downloadUrl)
      : `${this.baseUrl}/${encodeURIComponent(job.id)}/result`;
    return firstValueFrom(this.client.request<Blob>({
      method: 'GET',
      url,
      headers: {Accept: 'application/zip'},
      responseType: 'blob',
      hasErrorHandler: true,
      ignore404: true
    }));
  }
}
